import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import client, { getSession } from "@/lib/api";
import Divider from "@/components/Divider";

export default function ReaderHome() {
  const [chapters, setChapters] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [techniques, setTechniques] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!getSession()) { navigate("/login"); return; }
    Promise.all([
      client.get("/content/chapters"),
      client.get("/content/recipes?preview=true"),
      client.get("/content/menus"),
      client.get("/content/techniques"),
    ]).then(([c, r, m, t]) => {
      setChapters(c.data.chapters);
      setRecipes(r.data.recipes);
      setMenus(m.data.menus);
      setTechniques(t.data.techniques);
    }).catch(() => navigate("/login"));
  }, [navigate]);

  return (
    <div className="bg-mizizi-cream min-h-screen">
      <header className="max-w-6xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-12">
        <div className="overline text-mizizi-terracotta">The Reader</div>
        <h1 className="font-serif font-light text-5xl md:text-7xl mt-4 leading-none text-mizizi-charcoal">Welcome home.</h1>
        <p className="mt-6 max-w-2xl text-lg text-mizizi-charcoal/70">
          Your table of contents. Cook from your phone, your laptop, or a printed page — the book adapts to you.
        </p>
      </header>

      <Divider className="my-8" />

      <section className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="overline text-mizizi-gold mb-6">Chapters</div>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-8">
          {chapters.map((c, i) => {
            const chapterRecipes = recipes.filter(r => r.chapter === c.slug);
            return (
              <div key={c.slug} data-testid={`chapter-${c.slug}`}>
                <div className="flex items-baseline gap-4 hairline-b pb-3">
                  <div className="font-serif text-mizizi-gold text-2xl">{String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <div className="font-serif text-2xl text-mizizi-charcoal">{c.title}</div>
                    <div className="text-sm italic text-mizizi-charcoal/60">{c.subtitle}</div>
                  </div>
                </div>
                <ul className="mt-4 space-y-2">
                  {chapterRecipes.map(r => (
                    <li key={r.slug}>
                      <Link
                        to={`/read/recipe/${r.slug}`}
                        data-testid={`toc-${r.slug}`}
                        className="flex items-center justify-between py-1 text-mizizi-charcoal hover:text-mizizi-terracotta transition-colors"
                      >
                        <span className="font-serif text-lg">{r.title}</span>
                        <span className="overline text-mizizi-charcoal/40">{r.prep_min + r.cook_min}′</span>
                      </Link>
                    </li>
                  ))}
                  {chapterRecipes.length === 0 && (
                    <li className="italic text-mizizi-charcoal/40 text-sm">More recipes in this chapter — full 30 coming with future revisions.</li>
                  )}
                </ul>
                {c.slug === "foundations" && (
                  <Link to="/read/foundations" data-testid="chapter-foundations-link" className="mt-4 inline-block overline text-mizizi-gold border-b border-mizizi-gold/50 pb-1 hover:text-mizizi-terracotta">
                    Open the Foundations index →
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <Divider />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-8">
        <div className="overline text-mizizi-gold mb-6">Menus</div>
        <div className="grid md:grid-cols-3 gap-8">
          {menus.map((m) => (
            <Link
              key={m.slug}
              to={`/read/menu/${m.slug}`}
              data-testid={`menu-${m.slug}`}
              className="block bg-mizizi-forest text-mizizi-cream p-8 hover:bg-mizizi-forestDark transition-colors"
            >
              <div className="overline text-mizizi-gold">Menu</div>
              <h3 className="font-serif text-3xl mt-4">{m.title}</h3>
              <p className="mt-3 text-mizizi-cream/70 italic">{m.subtitle}</p>
              <p className="mt-6 overline text-mizizi-gold">Timeline · Shopping list · Open →</p>
            </Link>
          ))}
        </div>
      </section>

      <Divider />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-8">
        <div className="overline text-mizizi-gold mb-6">Techniques</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {techniques.map((t) => (
            <Link
              key={t.slug}
              to={`/read/techniques/${t.slug}`}
              data-testid={`technique-link-${t.slug}`}
              className="block p-5 border border-mizizi-gold/30 hover:bg-mizizi-forest hover:text-mizizi-cream transition-colors group"
            >
              <div className="overline text-mizizi-gold">Technique</div>
              <div className="font-serif text-xl mt-1 leading-tight">{t.title}</div>
              <div className="text-sm text-mizizi-charcoal/60 italic mt-2 group-hover:text-mizizi-cream/70 leading-snug">{t.subtitle}</div>
            </Link>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24">
        <div className="mt-12 flex flex-wrap gap-4">
          <Link to="/read/pantry" data-testid="reader-pantry" className="pill pill-primary">Open the Pantry</Link>
          <Link to="/read/foundations" data-testid="reader-foundations" className="pill pill-outline">Foundations Index</Link>
          <Link to="/read/downloads" data-testid="reader-downloads" className="pill pill-outline">Download PDFs</Link>
        </div>
      </section>
    </div>
  );
}
